import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { ProbeLot, ProbeLotForm, ProbeLotSearchForm } from 'entities/probe_lot'

type ProbeLotsResponse = PagingResponse & {
    results: ProbeLot[]
}

export function useFetchProbeLotsApi(
    searchForm: Form<ProbeLotSearchForm>
): IndexApiSet<ProbeLotsResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'probe_lots/'
    const api = useIndexApi<ProbeLotsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function useDownloadCsvProbeLotsApi(searchForm: Form<ProbeLotSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'probe_lots/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'probeLot', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostProbeLotApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProbeLotForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ProbeLotForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<ProbeLotForm>) => {
        const apiPath = `probe_lots/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutProbeLotApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProbeLotForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ProbeLotForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<ProbeLotForm>) => {
        const apiPath = `probe_lots/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutProbeLotsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProbeLotForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ProbeLotForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProbeLotForm[]>) => {
        const apiPath = `probe_lots/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteProbeLotApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `probe_lots/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
